import { NetworkStatus, useMutation, useQuery } from '@apollo/client';
import { Dialog, DialogContent, DialogTitle, MenuItem, Select, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { gql } from '__generated__';
import { UserFilterInputType, UserStatusEnumType, UserType } from '__generated__/graphql';
import dayjs, { Dayjs } from 'dayjs';
import { CSSProperties, FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import Button from 'components/Button';
import DataTable from 'components/DataTable';
import ErrorMessage from 'components/ErrorMessage';
import FilterBar from 'components/FilterBar';
import LoadingButton from 'components/LoadingButton';
import LoadingIndicator from 'components/LoadingIndicator';

import debounce from 'utils/debounce';
import { formatNumber } from 'utils/format-helper';
import statusEnumToReadable from 'utils/status-enum-to-readable';

import BulkUploadUser from './BulkUploadUser';
import GenerateIdentityCardsForDepartment from './GenerateIdentityCardsForDepartment';
import GenerateUserReportForDepartment from './GenerateUserReportForDepartment';
import theme from './theme.module.scss';

type UserStats = { numberOfUsers: number; status: 'INACTIVE' | 'ACTIVE' };
type EmployeeStats = { numberOfEmployees: number; organisationCode: 'DOE' | 'TSC' | 'OLA' | 'MOE' };

const EXPIRY_DURATION_OPTIONS = [
  { label: '3 Months', value: 'THREE_MONTHS' },
  { label: '6 Months', value: 'SIX_MONTHS' },
  { label: '1 Year', value: 'ONE_YEAR' },
  { label: '2 Years', value: 'TWO_YEARS' },
  { label: '3 Years', value: 'THREE_YEARS' },
  { label: '4 Years', value: 'FOUR_YEARS' },
  { label: '5 Years', value: 'FIVE_YEARS' },
  { label: 'Custom', value: 'CUSTOM' },
];

function computeExpiryDate(issueDate: Dayjs, duration: string): string {
  switch (duration) {
    case 'THREE_MONTHS':
      return issueDate.add(3, 'month').toISOString();
    case 'SIX_MONTHS':
      return issueDate.add(6, 'month').toISOString();
    case 'ONE_YEAR':
      return issueDate.add(1, 'year').toISOString();
    case 'TWO_YEARS':
      return issueDate.add(2, 'year').toISOString();
    case 'THREE_YEARS':
      return issueDate.add(3, 'year').toISOString();
    case 'FOUR_YEARS':
      return issueDate.add(4, 'year').toISOString();
    case 'FIVE_YEARS':
      return issueDate.add(5, 'year').toISOString();
    default:
      return issueDate.add(5, 'year').toISOString();
  }
}

const DEPARTMENT_QUERY = gql(`
  query DepartmentUsers(
    $departmentId: ID!
    $limit: Int
    $cursor: ID
    $filters: UserFilterInputType
  ) {
    department(id: $departmentId) {
      id
      users(limit: $limit, cursor: $cursor, filters: $filters) {
        nodes {
          id
          fullName
          status
          createdAt
          employee {
            id
            fileNumber
            organisationCode
            photo
            signature
            designation {
              name
            }
            division {
              name
            }
            address {
              city
            }
          }
        }
        pageInfo {
          cursor
          totalCount
          hasNextPage
        }
        statistics {
          employeeStatisticsBreakdownByOrganisationCode(departmentId: $departmentId) {
            numberOfEmployees
            organisationCode
          }
          userStatisticsBreakdownByStatus(departmentId: $departmentId) {
            numberOfUsers
            status
          }
        }
      }
    }
  }
`);

const UPDATE_BULK_USER_STATUS = gql(`
  mutation UpdateBulkUserStatus($userIds: [ID!]!, $status: UserStatusEnumType!) {
    updateBulkUserStatus(ids: $userIds, status: $status)
  }
`);

const GENERATE_IDENTITY_CARDS_MUTATION = gql(`
  mutation GenerateIdentityCardsForSelectedUsers(
    $departmentId: ID!
    $userIds: [ID!]
    $issueDate: String!
    $expiryDate: String!
  ) {
    generateIdentityCardsForDepartment(
      departmentId: $departmentId
      userIds: $userIds
      issueDate: $issueDate
      expiryDate: $expiryDate
    )
  }
`);

// Matches FormPanel theme.module.scss
const fieldLabel: CSSProperties = {
  fontSize: '0.8rem',
  color: '#696969',
  marginBottom: '0.25rem',
  marginTop: 0,
};
const fieldErrorText: CSSProperties = {
  fontSize: '0.75rem',
  color: '#d32f2f',
  marginLeft: '1rem',
  marginTop: '2px',
};
const fieldValueWrapper: CSSProperties = {
  fontWeight: 500,
  fontSize: '1rem',
  marginBottom: '0.5rem',
};
const fieldBtnContainer: CSSProperties = {
  display: 'inline-flex',
  flexFlow: 'row nowrap',
  margin: '0.5rem 0',
};

const DepartmentUsers: FC = () => {
  const navigate = useNavigate();
  const { departmentId } = useParams<{ departmentId: string }>() as { departmentId: string };
  const [bulkGenerateDialogOpen, setBulkGenerateDialogOpen] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [bulkIssueDate, setBulkIssueDate] = useState<Dayjs>(dayjs());
  const [bulkExpiryDuration, setBulkExpiryDuration] = useState('FIVE_YEARS');
  const [bulkCustomExpiryDate, setBulkCustomExpiryDate] = useState<Dayjs | null>(null);
  const [bulkErrors, setBulkErrors] = useState<Record<string, string>>({});

  const { loading, error, data, refetch, fetchMore, variables, networkStatus } = useQuery(
    DEPARTMENT_QUERY,
    {
      variables: {
        departmentId,
        limit: 30,
        filters: {
          status: UserStatusEnumType.Active,
        } as UserFilterInputType,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const [generateIdentityCards, { loading: generateLoading, error: generateError }] = useMutation(
    GENERATE_IDENTITY_CARDS_MUTATION,
    {
      refetchQueries: ['DepartmentIdentityCards'],
      onCompleted() {
        navigate(`/departments/${departmentId}/identity-cards`);
      },
    }
  );

  const [
    updateBulkUserStatus,
    { loading: updateBulkUserStatusLoading, error: updateBulkUserStatusError },
  ] = useMutation(UPDATE_BULK_USER_STATUS, {
    update(cache, { data }, context) {
      // Get IDs from the variables
      const updatedUserIds = context.variables?.userIds;
      const updatedStatus = context.variables?.status;

      if (!data || !data.updateBulkUserStatus || !updatedUserIds) return;

      Array.isArray(updatedUserIds) &&
        updatedUserIds.forEach((userId: string) => {
          cache.modify({
            id: `UserType:${userId}`,
            fields: {
              status() {
                return updatedStatus;
              },
            },
          });
        });
    },
  });

  const getOrganisationCodeFilterWithLabels = (
    statistics: {
      numberOfEmployees: number;
      organisationCode: 'DOE' | 'TSC' | 'OLA' | 'MOE';
    }[]
  ) => {
    return [
      {
        label: 'All',
        value: undefined,
      },
      ...statistics.map(entry => ({
        label: `${entry.organisationCode} (${formatNumber(entry.numberOfEmployees)})`,
        value: entry.organisationCode,
      })),
    ];
  };

  const getStatusFilterWithLabels = (
    statistics: {
      numberOfUsers: number;
      status: 'INACTIVE' | 'ACTIVE';
    }[]
  ) => {
    const allStatuses = Object.values(UserStatusEnumType);

    return [
      {
        label: 'All',
        value: undefined,
      },
      ...allStatuses.map(status => {
        const stat = statistics.find(s => s.status === status);
        return {
          label: `${statusEnumToReadable(status).label} (${stat?.numberOfUsers ?? 0})`,
          value: status,
        };
      }),
    ];
  };

  if (loading && !data) return <LoadingIndicator />;

  if (error || !data) return <ErrorMessage error={error} refetch={refetch} />;

  const users = data.department.users.nodes;
  const statistics = data.department.users.statistics;
  const pageInfo = data.department.users.pageInfo;

  return (
    <>
      <div style={{ display: 'flex', gap: '5px' }}>
        <Button onClick={() => navigate(`/create/departments/${departmentId}/user`)}>
          Create User
        </Button>{' '}
        <GenerateIdentityCardsForDepartment departmentId={departmentId} />{' '}
        <GenerateUserReportForDepartment departmentId={departmentId} />{' '}
        <BulkUploadUser departmentId={departmentId} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 0 }}>
        <div>
          {departmentId === '20009c7b-5c40-4d60-b3cc-9ea4c801112e' && (
            <FilterBar
              title="Organisation Code"
              filters={getOrganisationCodeFilterWithLabels(
                statistics.employeeStatisticsBreakdownByOrganisationCode as EmployeeStats[]
              )}
              currentValue={variables?.filters?.organisationCode as string}
              onSelect={value => {
                refetch({
                  filters: {
                    organisationCode: value as 'DOE' | 'TSC' | 'OLA' | 'MOE',
                  } as UserFilterInputType,
                });
              }}
            />
          )}
          <FilterBar
            title="Status"
            filters={getStatusFilterWithLabels(
              statistics.userStatisticsBreakdownByStatus as UserStats[]
            )}
            currentValue={variables?.filters?.status as UserStatusEnumType}
            onSelect={value => {
              refetch({
                filters: {
                  status: value as UserStatusEnumType,
                },
              });
            }}
          />
        </div>
        <TextField
          placeholder="Name, File number or Designation"
          size="small"
          onChange={debounce(e => {
            refetch({
              filters: {
                ...variables?.filters,
                text: e.target.value,
              },
            });
          }, 500)}
        />
      </div>
      <DataTable
        data={users as UserType[]}
        onClick={user => {
          user.employee
            ? navigate(`/users/${user.id}`)
            : navigate(`/create/departments/${departmentId}/user/${user.id}/employee-information`);
        }}
        bulkSelectActions={[
          {
            label: 'Generate ID Cards',
            loading: generateLoading,
            error: generateError,
            action: async selectedIds => {
              setSelectedUserIds(selectedIds);
              setBulkGenerateDialogOpen(true);
            },
          },
          {
            label: 'Set as Active',
            loading: updateBulkUserStatusLoading,
            error: updateBulkUserStatusError,
            action: async selectedIds => {
              updateBulkUserStatus({
                variables: {
                  userIds: selectedIds,
                  status: UserStatusEnumType.Active,
                },
              });
            },
          },
          {
            label: 'Set as Inactive',
            loading: updateBulkUserStatusLoading,
            error: updateBulkUserStatusError,
            action: async selectedIds => {
              updateBulkUserStatus({
                variables: {
                  userIds: selectedIds,
                  status: UserStatusEnumType.Inactive,
                },
              });
            },
          },
        ]}
        columns={[
          {
            label: 'ID',
            fieldName: 'id',
            type: 'SELECT',
          },
          {
            label: 'Full Name',
            fieldName: 'fullName',
          },
          {
            label: 'File Number',
            fieldName: 'employee.fileNumber',
          },
          ...(departmentId === '20009c7b-5c40-4d60-b3cc-9ea4c801112e'
            ? [
                {
                  label: 'Organisation',
                  fieldName: 'employee.organisationCode',
                },
              ]
            : []),
          {
            label: 'Photo',
            fieldName: 'employee.photo',
            type: 'IMAGE',
          },
          {
            label: 'Signature',
            fieldName: 'employee.signature',
            type: 'IMAGE',
          },
          {
            label: 'Designation',
            fieldName: 'employee.designation.name',
          },
          {
            label: 'Division',
            fieldName: 'employee.division.name',
          },
          {
            label: 'City',
            fieldName: 'employee.address.city',
          },
          {
            label: 'Created At',
            fieldName: 'createdAt',
            type: 'DATETIME',
          },
          {
            label: 'Status',
            fieldName: 'status',
            type: 'STATUS',
          },
        ]}
        hasNextPage={pageInfo.hasNextPage as boolean}
        paginationLoading={loading}
        filterLoading={networkStatus === NetworkStatus.setVariables}
        onLoadMore={() =>
          fetchMore({
            variables: {
              cursor: pageInfo.cursor,
            },
          })
        }
        totalCount={pageInfo.totalCount as number}
      />
      <Dialog
        open={bulkGenerateDialogOpen}
        onClose={() => setBulkGenerateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Generate Identity Cards</DialogTitle>
        <DialogContent sx={{ minHeight: '50vh' }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div>
              <p style={fieldLabel}>Issue Date</p>
              <div style={fieldValueWrapper}>
                <DatePicker
                  value={bulkIssueDate}
                  onChange={val => {
                    setBulkIssueDate(val!);
                    setBulkErrors(prev => ({ ...prev, issueDate: '' }));
                  }}
                  renderInput={params => (
                    <TextField fullWidth size="small" {...params} error={!!bulkErrors.issueDate} />
                  )}
                  inputFormat="DD/MM/YYYY"
                />
                {bulkErrors.issueDate && <p style={fieldErrorText}>{bulkErrors.issueDate}</p>}
              </div>
            </div>

            <div>
              <p style={fieldLabel}>Expiry Duration</p>
              <div style={fieldValueWrapper}>
                <Select
                  fullWidth
                  size="small"
                  value={bulkExpiryDuration}
                  onChange={ev => {
                    setBulkExpiryDuration(ev.target.value as string);
                    setBulkErrors(prev => ({ ...prev, customExpiryDate: '' }));
                  }}
                >
                  {EXPIRY_DURATION_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>

            {bulkExpiryDuration === 'CUSTOM' && (
              <div>
                <p style={fieldLabel}>Custom Expiry Date</p>
                <div style={fieldValueWrapper}>
                  <DatePicker
                    value={bulkCustomExpiryDate}
                    onChange={val => {
                      setBulkCustomExpiryDate(val);
                      if (val) setBulkErrors(prev => ({ ...prev, customExpiryDate: '' }));
                    }}
                    renderInput={params => (
                      <TextField
                        fullWidth
                        size="small"
                        {...params}
                        error={!!bulkErrors.customExpiryDate}
                      />
                    )}
                    inputFormat="DD/MM/YYYY"
                  />
                  {bulkErrors.customExpiryDate && (
                    <p style={fieldErrorText}>{bulkErrors.customExpiryDate}</p>
                  )}
                </div>
              </div>
            )}

            {generateError && <ErrorMessage error={generateError} type="alert" />}

            <div style={fieldBtnContainer}>
              <LoadingButton
                loading={generateLoading}
                className={theme.submitButton}
                onClick={() => {
                  const newErrors: Record<string, string> = {};
                  if (!bulkIssueDate) newErrors.issueDate = 'This field is required.';
                  if (bulkExpiryDuration === 'CUSTOM' && !bulkCustomExpiryDate)
                    newErrors.customExpiryDate = 'This field is required.';
                  if (Object.keys(newErrors).length > 0) {
                    setBulkErrors(newErrors);
                    return;
                  }

                  const expiryDate =
                    bulkExpiryDuration === 'CUSTOM'
                      ? bulkCustomExpiryDate!.toISOString()
                      : computeExpiryDate(bulkIssueDate, bulkExpiryDuration);

                  generateIdentityCards({
                    variables: {
                      departmentId,
                      userIds: selectedUserIds,
                      issueDate: bulkIssueDate.toISOString(),
                      expiryDate,
                    },
                  }).then(() => {
                    setBulkGenerateDialogOpen(false);
                    setBulkIssueDate(dayjs());
                    setBulkExpiryDuration('FIVE_YEARS');
                    setBulkCustomExpiryDate(null);
                    setBulkErrors({});
                  });
                }}
              >
                Generate
              </LoadingButton>
              <Button onClick={() => setBulkGenerateDialogOpen(false)}>Cancel</Button>
            </div>
          </LocalizationProvider>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DepartmentUsers;
